import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, openProposalPdf } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  MessageCircle, ChevronRight, ChevronLeft, Trash2, Plus, Check,
  Phone, Mail, Smartphone, Info, FileText, CheckCircle2,
} from 'lucide-react';
import ProposalConditionsEditor from '@/components/ProposalConditionsEditor';

const STEPS = [
  { key: 'client',     label: 'Client & campaign' },
  { key: 'channels',   label: 'Channels' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'review',     label: 'Review' },
];

const CHANNEL_SCOPE = {
  whatsapp_no_badge:   'whatsapp',
  whatsapp_named:      'whatsapp',
  whatsapp_authorized: 'whatsapp',
  rcs_image:           'rcs',
  sms_promotional:     'sms',
  ivr_voice_25s:       'ivr',
};

const DEFAULT_BILLING_EVENTS = {
  whatsapp_no_badge:   'per message delivered',
  whatsapp_named:      'per message delivered',
  whatsapp_authorized: 'per message delivered',
  rcs_image:           'per message delivered',
  sms_promotional:     'per segment',
  ivr_voice_25s:       'per connected call',
};

function fmtPaise(rate) {
  const r = Number(rate || 0);
  if (!Number.isFinite(r)) return '₹0.00';
  if (r < 1) {
    const paise = Math.round(r * 100);
    return `₹${r.toFixed(2)} (${paise} paise)`;
  }
  return `₹${r.toFixed(2)}`;
}

function fmtINR(n) {
  if (n == null) return '—';
  try { return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`; }
  catch { return `₹${n}`; }
}

export default function MessagingProposalWizard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [rateItems, setRateItems] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [clients, setClients] = useState([]);
  const [draft, setDraft] = useState({
    proposal_type: 'messaging_campaign',
    client_id: '',
    client_name: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    to_emails: [],
    campaign_name: '',
    campaign_objective: '',
    campaign_offer: '',
    offer_expiry_date: '',
    target_locations: [],
    languages: [],
    preferred_launch_at: '',
    authorised_approver_name: '',
    authorised_approver_phone: '',
    destination_url: '',
    total_budget_hint: '',
    campaign_date_from: '',
    campaign_date_to: '',
    validity_date: '',
    discount_pct: 0,
    gst_pct: 18,
    subject_line: 'Campaign Quotation',
    lines: [],
    compliance_ticks: {},
    conditions_snapshot: [],
    prepared_by: {
      name: user?.name || '',
      designation: user?.role || '',
      phone: user?.phone || '',
      email: user?.email || '',
    },
    services: ['messaging_campaign'],
  });
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const [generated, setGenerated] = useState(false);

  // Load rate items + compliance + clients + master terms (to seed conditions)
  useEffect(() => {
    (async () => {
      try {
        const [rates, comp, cls, terms] = await Promise.all([
          api.get('/messaging-rate-items'),
          api.get('/messaging-compliance'),
          api.get('/clients').catch(() => ({ data: [] })),
          api.get('/terms/current', { params: { kind: 'messaging_campaign' } }).catch(() => ({ data: {} })),
        ]);
        setRateItems(rates.data || []);
        setCompliance(comp.data || []);
        setClients(cls.data || []);
        // Seed conditions from the active messaging T&C body
        const body = terms?.data?.body || '';
        const clauses = body
          .split(/\n+/)
          .map((s) => s.replace(/^\s*\d{1,3}\s*[.)]\s+/, '').trim())
          .filter((s) => s && !/sample|settings\s*→|rate catalog/i.test(s))
          .map((text, i) => ({
            id: (typeof crypto !== 'undefined' && crypto.randomUUID
              ? crypto.randomUUID()
              : Math.random().toString(36).slice(2)),
            order: i + 1,
            text,
            source: 'master',
            included: true,
          }));
        setDraft((d) => ({ ...d, conditions_snapshot: clauses }));
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not load setup');
      }
    })();
  }, []);

  const rateByKey = useMemo(() => {
    const m = {};
    (rateItems || []).forEach((r) => { m[r.key] = r; });
    return m;
  }, [rateItems]);

  // ── Pricing (client-side preview — server is source of truth on save) ──
  const totals = useMemo(() => {
    let subtotal = 0;
    const perLine = (draft.lines || []).map((ln) => {
      const r = rateByKey[ln.channel_key];
      const rate = Number(ln.rate ?? r?.default_rate ?? 0);
      const recipients = Number(ln.recipients || 0);
      let billable = recipients;
      if (ln.channel_key === 'sms_promotional') {
        const seg = Number(ln.sms_segments || 1);
        billable = recipients * seg;
      }
      const amount = Math.round(rate * billable * 100) / 100;
      const extrasTotal = (ln.extras || []).reduce((s, e) => s + Number(e.price || 0), 0);
      subtotal += amount + extrasTotal;
      return { amount, extrasTotal, lineTotal: amount + extrasTotal };
    });
    const disc = (subtotal * Number(draft.discount_pct || 0)) / 100;
    const taxable = subtotal - disc;
    const gst = (taxable * Number(draft.gst_pct || 0)) / 100;
    const total = Math.round(taxable + gst);
    return { subtotal, discount: disc, taxable, gst, total, perLine };
  }, [draft, rateByKey]);

  const canNext = useMemo(() => {
    if (step === 0) return !!draft.client_name && !!draft.validity_date;
    if (step === 1) {
      if ((draft.lines || []).length === 0) return false;
      return (draft.lines || []).every(
        (l) => l.channel_key && Number(l.recipients) > 0 && (l.billing_event || '').trim(),
      );
    }
    if (step === 2) {
      // required compliance ticks: 'all' + per-channel scopes used
      const scopes = new Set(['all']);
      (draft.lines || []).forEach((l) => {
        const sc = CHANNEL_SCOPE[l.channel_key];
        if (sc) scopes.add(sc);
      });
      const needed = compliance.filter((c) => c.required && scopes.has(c.scope));
      return needed.every((c) => draft.compliance_ticks?.[c.id]);
    }
    return true;
  }, [step, draft, compliance]);

  const addLine = (channel_key) => {
    const r = rateByKey[channel_key];
    if (!r) return;
    const line = {
      service_key: 'messaging_campaign',
      channel_key,
      channel_label: r.label,
      unit_label: r.unit_label,
      recipients: 10000,
      rate: r.default_rate,
      billing_event: DEFAULT_BILLING_EVENTS[channel_key] || '',
      sender_detail: '',
      description: '',
      extras: [],
    };
    if (channel_key === 'sms_promotional') {
      line.sms_draft_message = '';
      line.sms_segments = 1;
      line.sms_encoding = 'gsm';
    }
    setDraft((d) => ({ ...d, lines: [...(d.lines || []), line] }));
  };

  const updateLine = (idx, patch) =>
    setDraft((d) => ({
      ...d,
      lines: d.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)),
    }));

  const removeLine = (idx) =>
    setDraft((d) => ({ ...d, lines: d.lines.filter((_, i) => i !== idx) }));

  const addExtra = (idx) =>
    updateLine(idx, {
      extras: [...(draft.lines[idx].extras || []), { description: '', price: 0 }],
    });
  const updateExtra = (idx, exi, patch) => {
    const extras = [...(draft.lines[idx].extras || [])];
    extras[exi] = { ...extras[exi], ...patch };
    updateLine(idx, { extras });
  };
  const removeExtra = (idx, exi) => {
    const extras = (draft.lines[idx].extras || []).filter((_, i) => i !== exi);
    updateLine(idx, { extras });
  };

  // ── SMS segment live calc via backend (keeps GSM/Unicode rules consistent) ──
  const recalcSMS = async (idx) => {
    const l = draft.lines[idx];
    if (!l || l.channel_key !== 'sms_promotional') return;
    try {
      const r = await api.post('/sms-calc', {
        text: l.sms_draft_message || '',
        recipients: Number(l.recipients || 0),
      });
      updateLine(idx, {
        sms_segments: r.data.segments,
        sms_encoding: r.data.encoding,
      });
    } catch {
      /* ignore */
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const payload = { ...draft };
      // Convert prepared_by into flat fields the backend reads
      payload.prepared_by_name = draft.prepared_by?.name;
      payload.prepared_by_phone = draft.prepared_by?.phone;
      payload.prepared_by_email = draft.prepared_by?.email;
      const r = await api.post('/proposals', payload);
      setSavedId(r.data.id);
      toast.success(`Saved as ${r.data.number}`);
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save draft');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const patchDraft = async (patch) => {
    if (!savedId) return;
    try {
      await api.patch(`/proposals/${savedId}`, patch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update draft');
    }
  };

  const generatePdf = async () => {
    let id = savedId;
    if (!id) {
      const d = await saveDraft();
      id = d?.id;
      if (!id) return;
    } else {
      await patchDraft({
        ...draft,
        compliance_ticks: draft.compliance_ticks,
        conditions_snapshot: draft.conditions_snapshot,
      });
    }
    try {
      await api.post(`/proposals/${id}/generate`);
      setGenerated(true);
      toast.success('Quotation generated');
      await openProposalPdf(id, { mode: 'open' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate quotation');
    }
  };

  const requiredScopes = useMemo(() => {
    const scopes = new Set(['all']);
    (draft.lines || []).forEach((l) => {
      const sc = CHANNEL_SCOPE[l.channel_key];
      if (sc) scopes.add(sc);
    });
    return scopes;
  }, [draft.lines]);

  const channelIcon = (k) => {
    if (k?.startsWith('whatsapp')) return <MessageCircle className="w-4 h-4" />;
    if (k === 'rcs_image') return <Smartphone className="w-4 h-4" />;
    if (k === 'sms_promotional') return <Mail className="w-4 h-4" />;
    if (k === 'ivr_voice_25s') return <Phone className="w-4 h-4" />;
    return <MessageCircle className="w-4 h-4" />;
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="messaging-proposal-wizard">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
            <MessageCircle className="w-4 h-4" />
            <span>Messaging &amp; Voice Campaign</span>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900">New campaign quotation</h1>
          <p className="text-sm text-slate-500">
            WhatsApp, RCS, SMS and IVR — billed per the agreed billable unit.
          </p>
        </div>
        <Button variant="ghost" onClick={() => navigate('/proposals')} data-testid="wizard-close-btn">
          Cancel
        </Button>
      </div>

      <div className="flex items-center gap-1.5 mb-6 overflow-x-auto no-scrollbar -mx-6 px-6" aria-label="Wizard steps">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.key}>
            <div
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs sm:text-sm whitespace-nowrap shrink-0 ${
                i === step
                  ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                  : i < step
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-slate-100 text-slate-500'
              }`}
              data-testid={`wizard-step-${s.key}`}
            >
              {i < step ? <Check className="w-3.5 h-3.5" /> : <span className="font-mono text-[11px]">{i + 1}</span>}
              {s.label}
            </div>
            {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      {/* ── STEP 1: Client & campaign ── */}
      {step === 0 && (
        <Card className="p-5 space-y-4" data-testid="step-client">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Client *</Label>
              <Select
                value={draft.client_id || '__none__'}
                onValueChange={(v) => {
                  const c = clients.find((x) => x.id === v);
                  setDraft((d) => ({
                    ...d,
                    client_id: v === '__none__' ? '' : v,
                    client_name: c?.business_name || c?.name || d.client_name,
                    contact_name: c?.contact_name || d.contact_name,
                    contact_phone: c?.phone || d.contact_phone,
                    contact_email: c?.email || d.contact_email,
                  }));
                }}
              >
                <SelectTrigger data-testid="client-select"><SelectValue placeholder="Pick a client (or type a name)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Pick later / type below —</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.business_name || c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Client name *</Label>
              <Input
                value={draft.client_name}
                onChange={(e) => setDraft({ ...draft, client_name: e.target.value })}
                data-testid="client-name-input"
              />
            </div>
            <div>
              <Label>Contact / Attn</Label>
              <Input
                value={draft.contact_name}
                onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })}
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <Label>Contact phone</Label>
              <Input
                value={draft.contact_phone}
                onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value })}
                data-testid="contact-phone-input"
              />
            </div>
            <div>
              <Label>Contact email</Label>
              <Input
                value={draft.contact_email}
                onChange={(e) => setDraft({ ...draft, contact_email: e.target.value })}
                data-testid="contact-email-input"
              />
            </div>
            <div>
              <Label>Valid till *</Label>
              <Input
                type="date"
                value={draft.validity_date}
                onChange={(e) => setDraft({ ...draft, validity_date: e.target.value })}
                data-testid="validity-date-input"
              />
            </div>
          </div>

          <div className="h-px bg-slate-100 my-2" />
          <div className="text-sm font-semibold text-slate-800">Campaign</div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Campaign name</Label>
              <Input
                value={draft.campaign_name}
                onChange={(e) => setDraft({ ...draft, campaign_name: e.target.value })}
                placeholder="e.g. Diwali promo – October 2026"
                data-testid="campaign-name-input"
              />
            </div>
            <div>
              <Label>Objective</Label>
              <Input
                value={draft.campaign_objective}
                onChange={(e) => setDraft({ ...draft, campaign_objective: e.target.value })}
                placeholder="e.g. Drive e-commerce signups"
                data-testid="campaign-objective-input"
              />
            </div>
            <div>
              <Label>Offer</Label>
              <Input
                value={draft.campaign_offer}
                onChange={(e) => setDraft({ ...draft, campaign_offer: e.target.value })}
                placeholder="e.g. Flat 20% off"
                data-testid="campaign-offer-input"
              />
            </div>
            <div>
              <Label>Offer expiry</Label>
              <Input
                type="date"
                value={draft.offer_expiry_date || ''}
                onChange={(e) => setDraft({ ...draft, offer_expiry_date: e.target.value })}
                data-testid="offer-expiry-input"
              />
            </div>
            <div>
              <Label>Preferred launch date / time</Label>
              <Input
                type="datetime-local"
                value={draft.preferred_launch_at || ''}
                onChange={(e) => setDraft({ ...draft, preferred_launch_at: e.target.value })}
                data-testid="preferred-launch-input"
              />
            </div>
            <div>
              <Label>Campaign window from</Label>
              <Input
                type="date"
                value={draft.campaign_date_from || ''}
                onChange={(e) => setDraft({ ...draft, campaign_date_from: e.target.value })}
                data-testid="campaign-from-input"
              />
            </div>
            <div>
              <Label>Campaign window to</Label>
              <Input
                type="date"
                value={draft.campaign_date_to || ''}
                onChange={(e) => setDraft({ ...draft, campaign_date_to: e.target.value })}
                data-testid="campaign-to-input"
              />
            </div>
            <div>
              <Label>Target locations (comma-sep.)</Label>
              <Input
                value={(draft.target_locations || []).join(', ')}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    target_locations: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })
                }
                placeholder="Hyderabad, Secunderabad, Vijayawada"
                data-testid="target-locations-input"
              />
            </div>
            <div>
              <Label>Language(s) (comma-sep.)</Label>
              <Input
                value={(draft.languages || []).join(', ')}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    languages: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })
                }
                placeholder="English, Telugu"
                data-testid="languages-input"
              />
            </div>
            <div className="col-span-2">
              <Label>Destination URL (optional)</Label>
              <Input
                value={draft.destination_url || ''}
                onChange={(e) => setDraft({ ...draft, destination_url: e.target.value })}
                placeholder="https://example.com/offer"
                data-testid="destination-url-input"
              />
            </div>
            <div>
              <Label>Authorised approver name</Label>
              <Input
                value={draft.authorised_approver_name || ''}
                onChange={(e) => setDraft({ ...draft, authorised_approver_name: e.target.value })}
                data-testid="approver-name-input"
              />
            </div>
            <div>
              <Label>Authorised approver phone</Label>
              <Input
                value={draft.authorised_approver_phone || ''}
                onChange={(e) => setDraft({ ...draft, authorised_approver_phone: e.target.value })}
                data-testid="approver-phone-input"
              />
            </div>
          </div>
        </Card>
      )}

      {/* ── STEP 2: Channels ── */}
      {step === 1 && (
        <div className="space-y-4" data-testid="step-channels">
          <Card className="p-4">
            <div className="text-sm text-slate-700 mb-3">Pick a channel to add a line:</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {rateItems.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => addLine(r.key)}
                  className="flex items-start gap-3 rounded-md border p-3 bg-white hover:bg-emerald-50 hover:border-emerald-300 transition-colors text-left"
                  data-testid={`add-line-${r.key}`}
                >
                  <div className="shrink-0 p-2 rounded-md bg-emerald-100 text-emerald-700">
                    {channelIcon(r.key)}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-800">{r.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {fmtPaise(r.default_rate)} / {r.unit_label}
                      {r.is_range && ` · range ${fmtPaise(r.min_rate)}–${fmtPaise(r.max_rate)}`}
                    </div>
                    {r.internal_note && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                        <Info className="w-3 h-3" /> Internal note (not printed)
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </Card>

          {(draft.lines || []).length === 0 && (
            <Card className="p-8 text-center text-sm text-slate-500" data-testid="empty-lines-hint">
              Add at least one channel line to continue.
            </Card>
          )}

          {draft.lines.map((ln, idx) => {
            const r = rateByKey[ln.channel_key];
            const isSMS = ln.channel_key === 'sms_promotional';
            const inRange = r?.is_range;
            const rateInvalid =
              inRange && (Number(ln.rate) < Number(r.min_rate) || Number(ln.rate) > Number(r.max_rate));
            return (
              <Card key={idx} className="p-4 space-y-3" data-testid={`line-card-${idx}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-md bg-emerald-100 text-emerald-700">
                      {channelIcon(ln.channel_key)}
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-slate-800">{r?.label || ln.channel_key}</div>
                      <div className="text-xs text-slate-500">
                        Billable unit: {ln.unit_label}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeLine(idx)}
                    data-testid={`remove-line-${idx}`}
                  >
                    <Trash2 className="w-4 h-4 text-rose-600" />
                  </Button>
                </div>

                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <Label>Recipients *</Label>
                    <Input
                      type="number"
                      min={0}
                      value={ln.recipients || 0}
                      onChange={(e) => updateLine(idx, { recipients: Number(e.target.value || 0) })}
                      onBlur={() => isSMS && recalcSMS(idx)}
                      data-testid={`line-${idx}-recipients`}
                    />
                  </div>
                  <div>
                    <Label>
                      Rate {inRange && <span className="text-[10px] text-slate-500">(range {fmtPaise(r.min_rate)}–{fmtPaise(r.max_rate)})</span>}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      disabled={!inRange}
                      value={ln.rate}
                      onChange={(e) => updateLine(idx, { rate: Number(e.target.value || 0) })}
                      data-testid={`line-${idx}-rate`}
                    />
                    {rateInvalid && (
                      <div className="text-[11px] text-rose-600 mt-1">
                        Rate must be within the allowed range.
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>Sender / route</Label>
                    <Input
                      value={ln.sender_detail || ''}
                      onChange={(e) => updateLine(idx, { sender_detail: e.target.value })}
                      placeholder="SHIADVTIS / +91 90000 12345"
                      data-testid={`line-${idx}-sender`}
                    />
                  </div>
                  <div>
                    <Label>Billing event *</Label>
                    <Input
                      value={ln.billing_event || ''}
                      onChange={(e) => updateLine(idx, { billing_event: e.target.value })}
                      placeholder="per message delivered"
                      data-testid={`line-${idx}-billing`}
                    />
                  </div>
                </div>

                {/* SMS calculator */}
                {isSMS && (
                  <div className="rounded-md bg-slate-50 p-3 space-y-2">
                    <Label>Draft message (internal — not printed on PDF)</Label>
                    <Textarea
                      rows={3}
                      value={ln.sms_draft_message || ''}
                      onChange={(e) => updateLine(idx, { sms_draft_message: e.target.value })}
                      onBlur={() => recalcSMS(idx)}
                      placeholder="Type the SMS body here. Non-English characters switch to Unicode (70-char segments)."
                      data-testid={`line-${idx}-sms-draft`}
                    />
                    <div className="flex items-center gap-3 text-xs text-slate-600">
                      <span>Segments: <b data-testid={`line-${idx}-sms-segments`}>{ln.sms_segments || 1}</b></span>
                      <span>·</span>
                      <span>Encoding: <b>{ln.sms_encoding || 'gsm'}</b></span>
                      <span>·</span>
                      <span>Billable units: <b>{Number(ln.recipients || 0) * Number(ln.sms_segments || 1)}</b></span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-auto"
                        onClick={() => recalcSMS(idx)}
                        data-testid={`line-${idx}-sms-recalc`}
                      >
                        Recalculate
                      </Button>
                    </div>
                  </div>
                )}

                <div>
                  <Label>Description (optional, printed under the item name)</Label>
                  <Textarea
                    rows={2}
                    value={ln.description || ''}
                    onChange={(e) => updateLine(idx, { description: e.target.value })}
                    placeholder="e.g. Welcome message to new signups during the campaign window."
                    data-testid={`line-${idx}-description`}
                  />
                </div>

                {/* Extras */}
                <div className="border-t pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <Label>Additional items</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addExtra(idx)}
                      data-testid={`line-${idx}-add-extra`}
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add extra
                    </Button>
                  </div>
                  {(ln.extras || []).map((ex, exi) => (
                    <div key={exi} className="grid grid-cols-[1fr_140px_40px] gap-2 mb-2">
                      <Textarea
                        rows={1}
                        value={ex.description || ''}
                        onChange={(e) => updateExtra(idx, exi, { description: e.target.value })}
                        placeholder="Description (e.g. carousel creative, additional language, call recording)"
                        data-testid={`line-${idx}-extra-${exi}-desc`}
                      />
                      <Input
                        type="number"
                        step="0.01"
                        value={ex.price || 0}
                        onChange={(e) => updateExtra(idx, exi, { price: Number(e.target.value || 0) })}
                        placeholder="Price"
                        data-testid={`line-${idx}-extra-${exi}-price`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeExtra(idx, exi)}
                        data-testid={`line-${idx}-extra-${exi}-remove`}
                      >
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-end gap-2 text-sm text-slate-700">
                  <span>Line amount:</span>
                  <b data-testid={`line-${idx}-amount`}>
                    {fmtINR(totals.perLine[idx]?.amount)}
                  </b>
                  {totals.perLine[idx]?.extrasTotal > 0 && (
                    <>
                      <span>+ extras</span>
                      <b>{fmtINR(totals.perLine[idx]?.extrasTotal)}</b>
                    </>
                  )}
                </div>
              </Card>
            );
          })}

          {draft.lines.length > 0 && (
            <Card className="p-4 bg-slate-50">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Subtotal (preview)</span>
                <b className="text-slate-900">{fmtINR(totals.subtotal)}</b>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ── STEP 3: Compliance ── */}
      {step === 2 && (
        <Card className="p-5 space-y-3" data-testid="step-compliance">
          <div className="text-sm text-slate-700">
            Tick the applicable compliance confirmations before generating. These are shown
            internally only and are never printed on the client PDF.
          </div>
          {compliance
            .filter((c) => requiredScopes.has(c.scope))
            .map((c) => (
              <label
                key={c.id}
                className="flex items-start gap-3 rounded-md border p-3 bg-white hover:bg-emerald-50/50 cursor-pointer"
                data-testid={`compliance-row-${c.id}`}
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={!!draft.compliance_ticks?.[c.id]}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      compliance_ticks: {
                        ...(draft.compliance_ticks || {}),
                        [c.id]: e.target.checked,
                      },
                    })
                  }
                  data-testid={`compliance-tick-${c.id}`}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-[10px] uppercase">
                      {c.scope}
                    </Badge>
                    {c.required && (
                      <Badge className="bg-amber-100 text-amber-700 text-[10px] uppercase">
                        Required
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-slate-700">{c.body}</div>
                </div>
              </label>
            ))}
        </Card>
      )}

      {/* ── STEP 4: Review ── */}
      {step === 3 && (
        <div className="space-y-4" data-testid="step-review">
          <Card className="p-5">
            <div className="font-semibold text-slate-800 mb-3">Line summary</div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="text-left text-xs text-slate-500 uppercase border-b">
                <tr>
                  <th className="py-2 pr-2">Channel</th>
                  <th className="py-2 pr-2">Sender / route</th>
                  <th className="py-2 pr-2">Billing</th>
                  <th className="py-2 pr-2 text-right">Qty</th>
                  <th className="py-2 pr-2 text-right">Rate</th>
                  <th className="py-2 pr-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {draft.lines.map((ln, i) => (
                  <React.Fragment key={i}>
                    <tr className="border-b">
                      <td className="py-2 pr-2">
                        <div className="font-medium">{ln.channel_label}</div>
                        {ln.description && (
                          <div className="text-xs text-slate-500 whitespace-pre-wrap">{ln.description}</div>
                        )}
                      </td>
                      <td className="py-2 pr-2">{ln.sender_detail || '—'}</td>
                      <td className="py-2 pr-2">{ln.billing_event}</td>
                      <td className="py-2 pr-2 text-right">
                        {ln.channel_key === 'sms_promotional'
                          ? `${Number(ln.recipients).toLocaleString('en-IN')} × ${ln.sms_segments || 1} seg`
                          : Number(ln.recipients).toLocaleString('en-IN')}
                      </td>
                      <td className="py-2 pr-2 text-right">{fmtPaise(ln.rate)}</td>
                      <td className="py-2 pr-2 text-right">{fmtINR(totals.perLine[i]?.amount)}</td>
                    </tr>
                    {(ln.extras || []).map((ex, exi) => (
                      <tr key={exi} className="border-b border-slate-100 text-slate-500 italic">
                        <td className="py-1 pr-2 pl-6" colSpan={5}>
                          Additional: {ex.description || '—'}
                        </td>
                        <td className="py-1 pr-2 text-right">{fmtINR(ex.price)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            </div>

            <div className="h-px bg-slate-100 my-3" />
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <Label>Discount %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={draft.discount_pct}
                  onChange={(e) => setDraft({ ...draft, discount_pct: Number(e.target.value || 0) })}
                  data-testid="review-discount-pct"
                />
              </div>
              <div>
                <Label>GST %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={draft.gst_pct}
                  onChange={(e) => setDraft({ ...draft, gst_pct: Number(e.target.value || 0) })}
                  data-testid="review-gst-pct"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Set to 0 to print "GST as applicable" on the PDF.
                </div>
              </div>
            </div>

            <div className="h-px bg-slate-100 my-3" />
            <div className="space-y-1 text-sm text-slate-700">
              <div className="flex justify-between"><span>Subtotal</span><b>{fmtINR(totals.subtotal)}</b></div>
              {totals.discount > 0 && (
                <div className="flex justify-between text-emerald-700">
                  <span>Discount ({draft.discount_pct}%)</span>
                  <b>− {fmtINR(totals.discount)}</b>
                </div>
              )}
              <div className="flex justify-between"><span>Taxable value</span><b>{fmtINR(totals.taxable)}</b></div>
              <div className="flex justify-between">
                <span>GST @ {draft.gst_pct}%</span>
                <b>{draft.gst_pct > 0 ? fmtINR(totals.gst) : 'as applicable'}</b>
              </div>
              <div className="flex justify-between pt-2 border-t text-base">
                <span className="font-semibold">Total</span>
                <b data-testid="review-total">
                  {fmtINR(draft.gst_pct > 0 ? totals.total : Math.round(totals.taxable))}
                </b>
              </div>
            </div>
          </Card>

          <ProposalConditionsEditor
            value={draft.conditions_snapshot}
            onChange={(next) => setDraft({ ...draft, conditions_snapshot: next })}
            title="Campaign terms and conditions for this proposal"
          />
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between mt-6">
        <Button
          variant="outline"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          data-testid="wizard-back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>

        <div className="flex items-center gap-2">
          {step === 3 ? (
            <>
              <Button
                variant="outline"
                onClick={saveDraft}
                disabled={saving}
                data-testid="save-draft-btn"
              >
                <FileText className="w-4 h-4 mr-1.5" /> Save draft
              </Button>
              <Button
                onClick={generatePdf}
                disabled={!canNext || saving}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="generate-pdf-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Generate quotation
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
              disabled={!canNext}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="wizard-next-btn"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </div>

      {generated && savedId && (
        <div className="fixed bottom-5 right-5 bg-white border rounded-lg shadow-lg p-3 z-40" data-testid="generated-toast">
          <div className="text-sm font-medium text-slate-800">Quotation generated</div>
          <div className="flex gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={() => navigate(`/proposals/${savedId}`)}>
              Open proposal
            </Button>
            <Button
              size="sm"
              onClick={() => openProposalPdf(savedId, { mode: 'open' })}
            >
              View PDF
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
